import {Button, Icon, IconButton, TextField} from '@wandb/components';
import {clsx} from 'clsx';
import React, {useCallback, useState} from 'react';

import type {Thread} from '../../../agentChat/store/thread';
import {TruncateTextTooltip} from '../../common/TruncateText/TruncateTextTooltip';
import {TruncationType} from '../../common/TruncateText/TruncateTextTypes';
import {getProjectChipLabel} from './projectChip';

export type HistoryItemMode = 'idle' | 'renaming' | 'confirming-delete';

interface Props {
  thread: Thread;
  displayTitle: string;
  currentEntity: string | null;
  mode: HistoryItemMode;
  onSelect: (rootTurnId: string) => void;
  onStartRename: (threadId: string) => void;
  onCommitRename: (threadId: string, newTitle: string) => void;
  onStartDelete: (threadId: string) => void;
  onConfirmDelete: (threadId: string) => void;
  onCancelMode: () => void;
}

// Pin to Source Sans 3 to match TextField; the app body still uses
// Source Sans Pro, so this prevents a font shift on rename toggle.
const CSS_CLASS_TITLE_AREA = 'min-w-0 flex-1 text-base';

/**
 * HistoryItem renders a single chat-history row. The `mode` prop toggles
 * between idle, inline-rename, and delete-confirmation; idle mode shows a
 * project chip when the row sits in a non-default project.
 */
export const HistoryItem: React.FC<Props> = ({
  thread,
  displayTitle,
  currentEntity,
  mode,
  onSelect,
  onStartRename,
  onCommitRename,
  onStartDelete,
  onConfirmDelete,
  onCancelMode,
}) => {
  const rootTurnId = thread.rootTurnId ?? thread.id;
  const isRenaming = mode === 'renaming';
  const isConfirmingDelete = mode === 'confirming-delete';
  const isInteractive = mode === 'idle';
  const chipLabel = getProjectChipLabel(thread, currentEntity);

  const handleSelect = useCallback(() => {
    onSelect(rootTurnId);
  }, [onSelect, rootTurnId]);

  const handleStartRename = useCallback(
    () => onStartRename(thread.id),
    [onStartRename, thread.id]
  );

  const handleCommitFromField = useCallback(
    (newTitle: string) => onCommitRename(thread.id, newTitle),
    [onCommitRename, thread.id]
  );

  const handleStartDelete = useCallback(
    () => onStartDelete(thread.id),
    [onStartDelete, thread.id]
  );

  const handleConfirmDelete = useCallback(
    () => onConfirmDelete(thread.id),
    [onConfirmDelete, thread.id]
  );

  // Escape cancels confirm-delete mode. `stopPropagation` prevents Escape
  // from bubbling up and closing the popover.
  const handleRowKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isConfirmingDelete && e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancelMode();
      }
    },
    [isConfirmingDelete, onCancelMode]
  );

  return (
    <div
      onClick={isInteractive ? handleSelect : undefined}
      onKeyDown={handleRowKeyDown}
      className={clsx(
        'group flex flex-col gap-[2px] rounded px-[2px] py-[4px]',
        isInteractive &&
          'cursor-pointer hover:bg-moon-100 dark:hover:bg-moon-800'
      )}>
      <div className="flex h-[32px] items-center gap-[4px]">
        {isRenaming ? (
          <RenameField
            initialTitle={displayTitle}
            onCommit={handleCommitFromField}
            onCancel={onCancelMode}
          />
        ) : (
          <>
            <div
              className={clsx(
                CSS_CLASS_TITLE_AREA,
                // Mirror TextField's input border + horizontal padding so the
                // title text doesn't shift when toggling into rename mode.
                'border border-transparent px-[8px]'
              )}>
              <TruncateTextTooltip
                fullText={displayTitle}
                truncationType={TruncationType.End}
                noLink
              />
            </div>
            {isConfirmingDelete ? (
              <DeleteConfirmation
                onConfirm={handleConfirmDelete}
                onCancel={onCancelMode}
              />
            ) : (
              <div
                className={clsx(
                  'items-center gap-[2px]',
                  'hidden group-hover:flex group-focus-within:flex'
                )}>
                <IconButton
                  variant="ghost"
                  size="sm"
                  icon="pencil-edit"
                  aria-label="Rename chat"
                  onClick={handleStartRename}
                />
                <IconButton
                  variant="ghost"
                  size="sm"
                  icon="delete"
                  aria-label="Delete chat"
                  onClick={handleStartDelete}
                />
              </div>
            )}
          </>
        )}
      </div>
      {!isRenaming && !isConfirmingDelete && chipLabel != null && (
        <div className="flex items-center gap-[4px] px-[8px] text-xs text-moon-500 dark:text-moon-400">
          <Icon icon="folder-project" size="xs" />
          <span className="min-w-0 truncate">{chipLabel}</span>
        </div>
      )}
    </div>
  );
};

interface DeleteConfirmationProps {
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * DeleteConfirmation renders the destructive Delete button with an adjacent
 * cancel icon. Auto-focuses the Delete button so Enter confirms.
 */
function DeleteConfirmation({onConfirm, onCancel}: DeleteConfirmationProps) {
  const focusOnMount = useCallback((el: HTMLElement | null) => el?.focus(), []);

  return (
    <div className="flex items-center gap-[2px]">
      <Button
        ref={focusOnMount}
        variant="destructive"
        size="sm"
        startIcon="delete"
        onPress={onConfirm}>
        Delete
      </Button>
      <IconButton
        variant="secondary"
        size="sm"
        icon="close"
        aria-label="Cancel delete"
        onClick={onCancel}
      />
    </div>
  );
}

interface RenameFieldProps {
  initialTitle: string;
  onCommit: (newTitle: string) => void;
  onCancel: () => void;
}

/**
 * RenameField owns the in-edit title value and is mounted only while
 * rename mode is active, so each open starts with a fresh input seeded
 * from `initialTitle`.
 */
function RenameField({initialTitle, onCommit, onCancel}: RenameFieldProps) {
  const [value, setValue] = useState(initialTitle);

  // TODO: Drop once TextField in @wandb/components wires `autoFocus`
  // through. SearchField/Switch/SelectFieldContent already do.
  // Today the prop falls through to AriaTextField and is silently dropped.
  const focusOnMount = useCallback((el: HTMLDivElement | null) => {
    const input = el?.querySelector<HTMLInputElement>('input');
    input?.focus();
    input?.select();
  }, []);

  const commit = useCallback(() => onCommit(value.trim()), [onCommit, value]);

  // Enter commits, Escape cancels. `stopPropagation` prevents Escape from
  // bubbling up and closing the popover.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        commit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      }
    },
    [commit, onCancel]
  );

  return (
    <>
      <div ref={focusOnMount} className={CSS_CLASS_TITLE_AREA}>
        <TextField
          aria-label="Edit chat title"
          value={value}
          onChange={setValue}
          onKeyDown={handleKeyDown}
          placeholder="Enter a title"
          fullWidth
        />
      </div>
      <div className="flex items-center gap-[2px]">
        <IconButton
          variant="primary"
          size="sm"
          icon="checkmark"
          aria-label="Save chat title"
          onClick={commit}
        />
        <IconButton
          variant="secondary"
          size="sm"
          icon="close"
          aria-label="Cancel rename"
          onClick={onCancel}
        />
      </div>
    </>
  );
}
